import { ProgressBar } from '@datacloud-incubation/frontend-common-library';
import { Navigate, type RouteObject } from 'react-router';
import { LAYOUT_MODE } from '@components/Layout/constants/layout.constant';
import Layout from '@components/Layout/Layout';
import { ROUTER_PATH } from '@constants/routerItems.constant';
import { loginTokenVerificationLoader } from '@pages/Auth/Login/loaders/loginTokenVerificationLoader';
import { tokenVerificationRouteLoader } from '@pages/Auth/ResetPassword/loaders/tokenVerificationRouteLoader';
import PermissionRoute from '@routes/components/PermissionRoute';
import ProtectedRoute from '@routes/components/ProtectedRoute';
import PublicRoute from '@routes/components/PublicRoute';
import { shareSessionLoader } from '@routes/loaders/shareSessionLoader.loader';
import { permissionService } from '@shared/services/Permission.service';
import { LAZY_COMPONENT } from './lazyComponent.constant';

const routeObject = (): RouteObject[] =>
  [
    {
      element: (
        <PublicRoute>
          <Layout layoutMode={LAYOUT_MODE.public} />
        </PublicRoute>
      ),
      children: [
        {
          path: ROUTER_PATH.login,
          element: <LAZY_COMPONENT.Login />,
          HydrateFallback: () => <ProgressBar />,
          loader: loginTokenVerificationLoader
        },
        {
          path: ROUTER_PATH.forgotPassword,
          element: <LAZY_COMPONENT.ForgotPassword />
        },
        {
          path: ROUTER_PATH.resetPassword,
          element: <LAZY_COMPONENT.ResetPassword />,
          loader: tokenVerificationRouteLoader
        }
      ]
    },
    {
      path: ROUTER_PATH.base,
      element: (
        <ProtectedRoute>
          <Layout layoutMode={LAYOUT_MODE.protected} />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Navigate to={ROUTER_PATH.flows} replace />
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity()['prompt-chain'].read}
            />
          ),
          children: [
            {
              path: ROUTER_PATH.flows,
              element: <LAZY_COMPONENT.Flows />
            },
            {
              path: ROUTER_PATH.viewFlow,
              element: <LAZY_COMPONENT.ViewFlow />
            }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity()['prompt-chain'].update}
            />
          ),
          children: [
            {
              path: ROUTER_PATH.editFlow,
              element: <LAZY_COMPONENT.EditFlow />
            }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity()['prompt-chain'].execute}
            />
          ),
          children: [
            {
              element: <LAZY_COMPONENT.FlowChat />,
              path: `${ROUTER_PATH.flowChat}/:sessionId?`
            },
            {
              element: null,
              path: `${ROUTER_PATH.flows}/:flowId/share/:id/:lastMessageId?`,
              loader: shareSessionLoader,
              HydrateFallback: () => <ProgressBar />
            }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity()['api-user'].read}
            />
          ),
          children: [
            { path: ROUTER_PATH.apiUsers, element: <LAZY_COMPONENT.ApiUsers /> },
            { path: ROUTER_PATH.viewApiUser, element: <LAZY_COMPONENT.ApiUserView /> }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity()['api-user'].update}
            />
          ),
          children: [{ path: ROUTER_PATH.editApiUser, element: <LAZY_COMPONENT.ApiUserForm /> }]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity().connection.read}
            />
          ),
          children: [
            { path: ROUTER_PATH.connections, element: <LAZY_COMPONENT.Connections /> },
            { path: ROUTER_PATH.viewConnection, element: <LAZY_COMPONENT.ConnectionView /> }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity().connection.update}
            />
          ),
          children: [
            { path: ROUTER_PATH.editConnection, element: <LAZY_COMPONENT.ConnectionEdit /> }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity().user.read}
            />
          ),
          children: [
            { path: ROUTER_PATH.users, element: <LAZY_COMPONENT.Users /> },
            { path: ROUTER_PATH.viewUser, element: <LAZY_COMPONENT.UserView /> },
            { path: ROUTER_PATH.editProfile, element: <LAZY_COMPONENT.EditProfile /> }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity().user.update}
            />
          ),
          children: [{ path: ROUTER_PATH.editUser, element: <LAZY_COMPONENT.UserForm /> }]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity().role.read}
            />
          ),
          children: [
            {
              path: ROUTER_PATH.roles,
              element: <LAZY_COMPONENT.Roles />
            },
            { path: ROUTER_PATH.viewRole, element: <LAZY_COMPONENT.RoleView /> }
          ]
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity().role.update}
            />
          ),
          children: [{ path: ROUTER_PATH.editRole, element: <LAZY_COMPONENT.RoleForm /> }]
        },
        {
          path: ROUTER_PATH.viewProfile,
          element: <LAZY_COMPONENT.ViewProfile />
        },
        {
          element: (
            <PermissionRoute
              isPermissionGranted={permissionService.getPermissionEntity()['user-action-logs'].read}
            />
          ),
          children: [
            {
              path: ROUTER_PATH.logs,
              element: <LAZY_COMPONENT.Logs />
            }
          ]
        }
      ]
    },
    {
      path: ROUTER_PATH.notFound,
      element: <LAZY_COMPONENT.NotFound />
    }
  ] as const;

export { routeObject };
